import Link from 'next/link';
import { createClient } from '@/prismicio';
import { asLink, asText, type LinkField } from '@prismicio/client';
import { SliceZone } from '@prismicio/react';
import { getFooterComponents } from '@/slices/component-factory';
import { DevsuLogoComplete } from '@/assets/DevsuLogoComplete';
import { resolvePageUrl } from '@/utils/page-url';
import { FooterNewsletterRedesign } from './FooterNewsletterRedesign';

const footerLinkClass =
  'py-1 text-[12px] font-light leading-[1.5] hover:text-[#00f557]';

interface FooterRedesignProps {
  navData?: Record<string, unknown>;
}

type FooterLink = { label: string; href: string };
type FooterColumn = { title: string; links: FooterLink[] };
type FooterSlice = {
  slice_type: string;
  variation: string;
  primary: Record<string, unknown>;
};

// Mirrors `GlobalNavBarRedesign`'s `resolveHref` so the footer respects the
// same virtual-parent tag convention (`src/utils/page-url.ts`); duplicated to
// keep the client-only navbar module untouched.
type PrismicLinkField = LinkField | null | undefined;
const resolveHref = (link: unknown): string => {
  const linkField = link as PrismicLinkField;
  if (!linkField) {
    return '#';
  }
  if (
    linkField.link_type === 'Document' &&
    linkField.type === 'page' &&
    linkField.uid
  ) {
    const resolved = resolvePageUrl({
      uid: linkField.uid,
      url: linkField.url ?? null,
      tags: linkField.tags,
    });
    if (resolved) {
      return resolved;
    }
  }
  return asLink(linkField) ?? '#';
};

// The Footer tab of `global_navigation` (slice zone `slices1`) authors each
// menu column as a `submenu` slice using the `default` variation:
//   - `primary.title`      → column heading
//   - `primary.element[]`  → column links (`element_label` + `element_link`)
// Other `submenu` variations in this tab (`credits`, `awardsStrip`) are
// authored for different parts of the footer and are skipped here. Column
// order follows the slice zone's authored order.
const CREDITS_FALLBACK: FooterLink[] = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Use', href: '/terms-of-service' },
  { label: 'Cookies Settings', href: '/privacy-policy' },
];

// Credits row links come from the `submenu` slice whose `primary.tag ===
// 'credits'` (its variation is also `credits`, but tag is the requested
// discriminator). Only `element[]` entries are pulled — the slice's own
// copyright field is intentionally ignored; copyright text is rendered
// separately from the current year in the JSX below.
const buildCreditsLinks = (slices: FooterSlice[]): FooterLink[] => {
  const credits = slices.find(
    (s) => s.slice_type === 'submenu' && s.primary.tag === 'credits',
  );
  if (!credits) {
    return CREDITS_FALLBACK;
  }
  const elements =
    (credits.primary.element as Array<Record<string, unknown>> | undefined) ??
    [];
  const links: FooterLink[] = [];
  for (const el of elements) {
    const label = (el.element_label as string) ?? '';
    if (!label) {
      continue;
    }
    links.push({ label, href: resolveHref(el.element_link) });
  }
  return links.length ? links : CREDITS_FALLBACK;
};

const buildFooterColumns = (slices: FooterSlice[]): FooterColumn[] => {
  const columns: FooterColumn[] = [];
  for (const slice of slices) {
    if (slice.slice_type !== 'submenu' || slice.variation !== 'default') {
      continue;
    }
    const title = (slice.primary.title as string) ?? '';
    if (!title) {
      continue;
    }
    const elements =
      (slice.primary.element as Array<Record<string, unknown>> | undefined) ??
      [];
    const links: FooterLink[] = [];
    for (const el of elements) {
      const label = (el.element_label as string) ?? '';
      if (!label) {
        continue;
      }
      links.push({ label, href: resolveHref(el.element_link) });
    }
    columns.push({ title, links });
  }
  return columns;
};

export const FooterRedesign = async ({ navData }: FooterRedesignProps = {}) => {
  const client = createClient();
  // Use passed navData or fetch if not provided (backwards compatibility)
  const data = navData || (await client.getSingle('global_navigation')).data;
  const { data: settings } = await client.getSingle('settings');

  // Type assertion for data structure
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const typedData = data as any;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const newsLetter = typedData.slices1.find(
    (slice: { slice_type: string }) => slice.slice_type === 'news_letter',
  );
  const socialMedia = typedData.slices1.find(
    (slice: { slice_type: string }) => slice.slice_type === 'social_media',
  );

  // Footer link columns come from the Footer tab (`slices1`) — each
  // `submenu[variation=default]` slice is one column. See `buildFooterColumns`
  // above for the field mapping.
  const footerSlices = (typedData.slices1 as FooterSlice[] | undefined) ?? [];
  const footerColumns = buildFooterColumns(footerSlices);
  const creditsLinks = buildCreditsLinks(footerSlices);

  const footerComponents = getFooterComponents();

  // Extract newsletter data for the light-mode inline form
  const newsletterPrimary = newsLetter?.primary;

  return (
    <footer className="bg-[#262626]">
      <div className="mx-auto max-w-[1440px] px-6 py-10 md:px-[80px] md:py-[40px]">
        <div className="flex flex-col gap-[51px]">
          {/* Main content row */}
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start xl:gap-[48px]">
            {/* Left — Logo + Social + Newsletter */}
            <div className="flex w-full max-w-xl flex-col gap-6 lg:max-w-[24rem] xl:max-w-none xl:min-w-0 xl:flex-1">
              {/* Logo + Social icons row */}
              <div className="flex items-center justify-between gap-4">
                <Link
                  href="/"
                  aria-label="Devsu Home Page"
                  className="inline-flex items-center gap-2 leading-none"
                >
                  <DevsuLogoComplete className="aspect-[107/25] h-6 md:h-7" />
                </Link>
                {socialMedia && (
                  <div className="flex items-center gap-4 [&_img]:brightness-0 [&_img]:invert">
                    <SliceZone
                      slices={[socialMedia]}
                      components={footerComponents}
                    />
                  </div>
                )}
              </div>

              {/* Newsletter */}
              {newsletterPrimary && (
                <FooterNewsletterRedesign
                  inviteText={
                    newsletterPrimary.invite_text
                      ? (asText(newsletterPrimary.invite_text) ?? undefined)
                      : undefined
                  }
                  emailPlaceholder={
                    newsletterPrimary.email_placeholder || undefined
                  }
                  buttonLabel={newsletterPrimary.button_label || undefined}
                  disclaimer={
                    newsletterPrimary.disclaimer
                      ? (asText(newsletterPrimary.disclaimer) ?? undefined)
                      : undefined
                  }
                  formId={newsletterPrimary.formId || undefined}
                  hubspotPortalIdForm={
                    settings.hubspotPortalIdForm || undefined
                  }
                />
              )}
            </div>

            {/* Right — Menu columns (dynamic from Prismic global_navigation). */}
            <div className="flex-1">
              <div className="flex flex-row flex-wrap gap-x-8 gap-y-8 text-[#eaeaea] lg:flex-nowrap lg:justify-end lg:gap-6 xl:gap-[20px]">
                {footerColumns.map((column) => (
                  <div key={column.title} className="flex flex-col gap-4">
                    <p className="text-[16px] font-medium leading-[1.5] text-[#eaeaea]">
                      {column.title}
                    </p>
                    <div className="flex flex-col opacity-80">
                      {column.links.map((link) => (
                        <Link
                          key={`${link.label}-${link.href}`}
                          href={link.href}
                          className={footerLinkClass}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Credits row */}
          <div className="flex flex-col gap-8">
            <div className="h-px w-full bg-[#474747]" />
            <div className="flex flex-col items-start justify-between gap-4 text-sm text-[#eaeaea] sm:flex-row sm:items-center">
              <p>© {new Date().getFullYear()} Devsu All rights reserved.</p>
              <div className="flex items-start gap-6 font-medium">
                {creditsLinks.map((link) => (
                  <Link
                    key={`${link.label}-${link.href}`}
                    href={link.href}
                    className="underline"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
